import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { obtenerIcono } from "./generarIconoVehiculo";
import { obtenerHistorico } from "../../../api/api-seguimiento";

const PIN_WIDTH = 32;
const PIN_HEIGHT = 36;
const LABEL_OFFSET_Y = 4;

const PUNTO_ACTUAL_MARKER_SIZE = 48;
const PUNTO_ACTUAL_LABEL_OFFSET_Y = 16;

const ORIGEN_DEFAULT_COLOR = "#16a34a";
const DESTINO_DEFAULT_COLOR = "#dc2626";
const GEORUTA_STROKE = "#2563eb";
const GEORUTA_ARROW_REPEAT = "88px";

const HISTORIC_VEHICLE_PATH_STROKE = "#dc2626";
const HISTORIC_VEHICLE_ARROW_REPEAT = "96px";
const HISTORIC_VEHICLE_START_MARKER_COLOR = "#16a34a";

// Directions API limits (origin + 23 waypoints + destination = 25 puntos por segmento)
const DIRECTIONS_MAX_WAYPOINTS = 23;
const DIRECTIONS_MAX_STOPS_PER_SEGMENT = 25;

function sumRouteLegsDistanceMeters(route) {
    if (!route?.legs?.length) return 0;
    let m = 0;
    for (const leg of route.legs) {
        if (leg?.distance?.value != null) m += leg.distance.value;
    }
    return m;
}

function splitPathForDirectionsSegments(path) {
    if (path.length < 2) return [];
    const segments = [];
    let start = 0;
    while (start < path.length - 1) {
        const end = Math.min(start + (DIRECTIONS_MAX_STOPS_PER_SEGMENT - 1), path.length - 1);
        const origin = path[start];
        const destination = path[end];
        const between = path.slice(start + 1, end);
        const waypoints = between.slice(0, DIRECTIONS_MAX_WAYPOINTS);
        segments.push({ origin, destination, waypoints });
        start = end;
    }
    return segments;
}

/**
 * Fetches driving directions polyline(s) from Google; concatenates segments when >25 stops.
 * onDone(latLngPath|null, distanceMeters|null)
 */
function fetchDirectionsDrivingPath(maps, pathPoints, onDone) {
    if (!pathPoints || pathPoints.length < 2) {
        onDone(null, null);
        return;
    }
    const svc = new maps.DirectionsService();
    const segments = splitPathForDirectionsSegments(pathPoints);

    let combinedFlat = [];
    let totalMeters = 0;

    const runSegment = (index) => {
        if (index >= segments.length) {
            if (combinedFlat.length >= 2) {
                onDone(combinedFlat, totalMeters);
            } else {
                onDone(null, null);
            }
            return;
        }
        const { origin, destination, waypoints } = segments[index];
        const wps = waypoints.map((p) => ({ location: p, stopover: true }));
        svc.route(
            {
                origin,
                destination,
                waypoints: wps,
                travelMode: maps.TravelMode.DRIVING,
                unitSystem: maps.UnitSystem.METRIC,
                optimizeWaypoints: false,
                provideRouteAlternatives: false,
            },
            (result, status) => {
                if (status !== maps.DirectionsStatus.OK || !result.routes?.[0]) {
                    onDone(null, null);
                    return;
                }
                const route = result.routes[0];
                totalMeters += sumRouteLegsDistanceMeters(route);
                const overview = route.overview_path || [];
                const pts = overview.map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
                if (combinedFlat.length > 0 && pts.length > 0) {
                    const last = combinedFlat[combinedFlat.length - 1];
                    const first = pts[0];
                    if (last.lat === first.lat && last.lng === first.lng) {
                        pts.shift();
                    }
                }
                combinedFlat = combinedFlat.concat(pts);
                runSegment(index + 1);
            }
        );
    };

    runSegment(0);
}

/**
 * Devuelve la URL data-SVG del icono del vehículo usando `obtenerIcono`, que ya trae
 * un fallback interno (`ICONO_MAP` -> auto/azul) para cuando `punto.icono` es
 * null/undefined o un id desconocido. Nada de PNGs del servidor -> nunca 404.
 */
function getVehicleIconUrl(punto) {
    if (punto == null || typeof punto !== "object") {
        return obtenerIcono(0, 0, 0, null, null);
    }
    return obtenerIcono(
        punto.ORIENT,
        punto.I_ENCENDIDO,
        punto.VELOCIDAD,
        punto.icono,
        null,
    );
}

function createLabelBelowOverlay(maps) {
    function LabelBelowOverlay(position, text, offsetY) {
        this.position = position;
        this.text = text != null ? String(text) : "";
        this.offsetY = offsetY != null ? Number(offsetY) : 0;
        this.div = null;
    }
    LabelBelowOverlay.prototype = Object.create(maps.OverlayView.prototype);
    LabelBelowOverlay.prototype.onAdd = function () {
        if (!this.text) return;
        const div = document.createElement("div");
        div.style.position = "absolute";
        div.style.left = "0";
        div.style.top = "0";
        div.style.pointerEvents = "none";

        const label = document.createElement("div");
        label.textContent = this.text;
        label.style.cssText = [
            "left: 0",
            "top: " + this.offsetY + "px",
            "position: absolute",
            "transform: translateX(-50%)",
            "width: max-content",
            "max-width: 140px",
            "padding: 2px 6px",
            "background: #fff",
            "border-radius: 4px",
            "box-shadow: 0 1px 3px rgba(0,0,0,0.25)",
            "font-size: 11px",
            "font-weight: 600",
            "color: #333",
            "text-align: center",
            "white-space: nowrap",
            "overflow: hidden",
            "text-overflow: ellipsis",
        ].join(";");
        div.appendChild(label);
        this.div = div;
        const panes = this.getPanes();
        if (panes && panes.markerLayer) panes.markerLayer.appendChild(div);
    };
    LabelBelowOverlay.prototype.draw = function () {
        if (!this.div || !this.position) return;
        const projection = this.getProjection();
        if (!projection) return;
        const point = projection.fromLatLngToDivPixel(
            new maps.LatLng(this.position.lat, this.position.lng)
        );
        if (!point) return;
        this.div.style.left = point.x + "px";
        this.div.style.top = point.y + "px";
    };
    LabelBelowOverlay.prototype.onRemove = function () {
        if (this.div && this.div.parentNode) this.div.parentNode.removeChild(this.div);
        this.div = null;
    };
    return LabelBelowOverlay;
}

function createTitleOnlyOverlay(maps) {
    function TitleOnlyOverlay(position, title, onClick) {
        this.position = position;
        this.title = title != null ? String(title) : "";
        this.onClick = onClick;
        this.div = null;
    }
    TitleOnlyOverlay.prototype = Object.create(maps.OverlayView.prototype);
    TitleOnlyOverlay.prototype.onAdd = function () {
        if (!this.title) return;
        const div = document.createElement("div");
        div.style.position = "absolute";
        div.style.cursor = "pointer";
        div.style.left = "0";
        div.style.top = "0";
        div.style.pointerEvents = "auto";

        const label = document.createElement("div");
        label.textContent = this.title;
        label.style.cssText = [
            "left: 0",
            "top: " + LABEL_OFFSET_Y + "px",
            "position: absolute",
            "transform: translateX(-50%)",
            "width: max-content",
            "max-width: 180px",
            "padding: 2px 6px",
            "background: #fff",
            "border-radius: 4px",
            "box-shadow: 0 1px 3px rgba(0,0,0,0.25)",
            "font-size: 11px",
            "font-weight: 600",
            "color: #333",
            "text-align: center",
            "white-space: nowrap",
            "overflow: hidden",
            "text-overflow: ellipsis",
        ].join(";");
        div.appendChild(label);

        div.addEventListener("click", (e) => {
            e.stopPropagation();
            if (typeof this.onClick === "function") this.onClick();
        });

        this.div = div;
        const panes = this.getPanes();
        if (panes && panes.markerLayer) panes.markerLayer.appendChild(div);
    };
    TitleOnlyOverlay.prototype.draw = function () {
        if (!this.div || !this.position) return;
        const projection = this.getProjection();
        if (!projection) return;
        const point = projection.fromLatLngToDivPixel(
            new maps.LatLng(this.position.lat, this.position.lng)
        );
        if (!point) return;
        this.div.style.left = point.x + "px";
        this.div.style.top = point.y + "px";
    };
    TitleOnlyOverlay.prototype.onRemove = function () {
        if (this.div && this.div.parentNode) this.div.parentNode.removeChild(this.div);
        this.div = null;
    };
    return TitleOnlyOverlay;
}

function getColoredMarkerIcon(hexColor) {
    const color = hexColor || "#D11F0F";
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
  <defs>
    <filter id="shadow" x="-20%" y="0" width="140%" height="140%">
      <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.35"/>
    </filter>
    <linearGradient id="shine" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" style="stop-color:white;stop-opacity:0.25"/>
      <stop offset="100%" style="stop-color:white;stop-opacity:0"/>
    </linearGradient>
  </defs>
  <path filter="url(#shadow)" fill="${color}" stroke="rgba(0,0,0,0.25)" stroke-width="1.2" stroke-linejoin="round"
    d="M20 2C10.06 2 2 10.5 2 21c0 11 18 29 18 29s18-18 18-29C38 10.5 29.94 2 20 2z"/>
  <path fill="url(#shine)" stroke="none" opacity="0.9"
    d="M20 2C10.06 2 2 10.5 2 21c0 11 18 29 18 29s18-18 18-29C38 10.5 29.94 2 20 2z"/>
  <circle cx="20" cy="18" r="7" fill="white" stroke="rgba(0,0,0,0.12)" stroke-width="0.8"/>
</svg>`;
    return {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
        scaledSize: new window.google.maps.Size(PIN_WIDTH, PIN_HEIGHT),
        anchor: new window.google.maps.Point(PIN_WIDTH / 2, PIN_HEIGHT),
    };
}

function getRespuestaByKey(viaje, key) {
    const arr = viaje?.respuestasParsed;
    if (!Array.isArray(arr)) return null;
    return arr.find((r) => r?.key === key) ?? null;
}

function getPlacaFromViaje(viaje) {
    const vehiculo = getRespuestaByKey(viaje, "seleccionevehiculo")?.value;
    if (vehiculo == null) return null;
    if (typeof vehiculo === "object") {
        return vehiculo.value ?? vehiculo.label ?? null;
    }
    return String(vehiculo);
}

function getFechaHoraInicio(viaje) {
    const fecha = getRespuestaByKey(viaje, "fechainicioviaje")?.value;
    const hora = getRespuestaByKey(viaje, "ingresehorapartida")?.value;
    if (!fecha || !hora) return null;
    return `${fecha} ${hora}`;
}

function getFechaHoraFin(viaje) {
    const fecha = getRespuestaByKey(viaje, "fechafinviaje")?.value;
    const hora = getRespuestaByKey(viaje, "horallegada")?.value;
    if (!fecha || !hora) return null;
    return `${fecha} ${hora}`;
}

function coordsToLatLngRings(coordenadas) {
    if (!Array.isArray(coordenadas) || coordenadas.length === 0) return [];
    const raw = Array.isArray(coordenadas[0]) ? coordenadas : [coordenadas];
    const rings = [];
    for (const ring of raw) {
        if (!Array.isArray(ring)) continue;
        const path = [];
        for (const c of ring) {
            if (c == null || typeof c !== "object") continue;
            const lat = parseFloat(c.x ?? c.lat ?? c.latitud);
            const lng = parseFloat(c.y ?? c.lng ?? c.longitud);
            if (Number.isNaN(lat) || Number.isNaN(lng) || !isFinite(lat) || !isFinite(lng)) continue;
            path.push({ lat, lng });
        }
        if (path.length > 0) rings.push(path);
    }
    return rings;
}

function ringCentroid(ring) {
    if (!Array.isArray(ring) || ring.length === 0) return null;
    let sLat = 0;
    let sLng = 0;
    for (const p of ring) {
        sLat += p.lat;
        sLng += p.lng;
    }
    return { lat: sLat / ring.length, lng: sLng / ring.length };
}

function historicRawToLatLngPath(raw) {
    if (!Array.isArray(raw) || raw.length === 0) return { path: [], startPoint: null };
    const path = [];
    let startPoint = null;

    const getHistoricTimeText = (point) => {
        if (point == null || typeof point !== "object") return "";
        const rawTime = point.fechaHora;
        if (rawTime == null || String(rawTime).trim() === "") return "";
        const value = String(rawTime).trim();
        const parsed = moment(value, moment.ISO_8601, true);
        if (parsed.isValid()) return parsed.format("HH:mm:ss");
        const flexible = moment(value);
        if (flexible.isValid()) return flexible.format("HH:mm:ss");
        return value;
    };

    for (const p of raw) {
        if (p == null || typeof p !== "object") continue;
        const lat = parseFloat(p.lat ?? p.Lat ?? p.latitud);
        const lng = parseFloat(p.lng ?? p.Lng ?? p.longitud);
        if (Number.isNaN(lat) || Number.isNaN(lng) || !isFinite(lat) || !isFinite(lng)) continue;
        path.push({ lat, lng });
        if (startPoint == null) {
            startPoint = { lat, lng, timeText: getHistoricTimeText(p) };
        }
    }
    return { path, startPoint };
}

/**
 * Google Map component to display viajes that live in the kanban's "iniciar viaje" column.
 *
 * @param {Array} viajes - Kanban card items (each with `respuestasParsed`).
 * @param {object|null} viajeSelected - Selected viaje (highlighted; enables historic track).
 * @param {object|null} puntoActual - Current vehicle position for the selected viaje.
 * @param {Function} onViajeClick - (viaje) => void, fired when a polygon/label/route of the viaje is clicked.
 * @param {object|null} center - { lat, lng } or null.
 * @param {number} zoom - Initial zoom level.
 */
export default function MapaViajeGoogle({
    viajes = [],
    viajeSelected = null,
    puntoActual = null,
    onViajeClick,
    center = null,
    zoom = 12,
    secuenciaSugerida = null, // { secuencia: [{tipo, coord, pedidoLabel, geocercaLabel}], startPos: {lat,lng} } | null
    vehiculosPuntos = [], // [{placa, punto}] positions for vehicles other than the selected viaje's
}) {
    const { t } = useTranslation();
    const mapRef = useRef(null);
    const [map, setMap] = useState(null);
    const shapesRef = useRef([]);
    const rutaSugeridaShapesRef = useRef([]);
    const vehiculosShapesRef = useRef([]);
    const puntoActualMarkerRef = useRef(null);
    const puntoActualLabelRef = useRef(null);
    const historicVehiclePolylineRef = useRef(null);
    const historicVehicleStartMarkerRef = useRef(null);
    const historicVehicleStartInfoWindowRef = useRef(null);
    const historicoFetchGenerationRef = useRef(0);
    const [historicVehiclePath, setHistoricVehiclePath] = useState([]);
    const [historicVehicleStartPoint, setHistoricVehicleStartPoint] = useState(null);
    const lastCenterZoomRef = useRef({ lat: null, lng: null, zoom: null });
    const didInitialFitRef = useRef(false);

    // Init map once
    useEffect(() => {
        if (!window.google || !window.google.maps || !mapRef.current) return;
        const defaultCenter = { lat: -16.5, lng: -63.6 };
        const initialCenter = center || defaultCenter;

        const googleMap = new window.google.maps.Map(mapRef.current, {
            center: initialCenter,
            zoom: zoom,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
            zoomControl: true,
        });
        setMap(googleMap);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Manual center/zoom updates
    useEffect(() => {
        if (!map || !window.google || !window.google.maps) return;
        const lat = center?.lat != null ? parseFloat(center.lat) : NaN;
        const lng = center?.lng != null ? parseFloat(center.lng) : NaN;
        const zoomVal = zoom != null ? Number(zoom) : null;
        const prev = lastCenterZoomRef.current;
        const centerChanged =
            center != null &&
            !Number.isNaN(lat) &&
            !Number.isNaN(lng) &&
            (prev.lat !== lat || prev.lng !== lng);
        const zoomChanged = zoomVal != null && prev.zoom !== zoomVal;

        if (centerChanged) {
            map.panTo(new window.google.maps.LatLng(lat, lng));
            lastCenterZoomRef.current = { ...prev, lat, lng };
        }
        if (zoomChanged) {
            map.setZoom(zoomVal);
            lastCenterZoomRef.current = { ...lastCenterZoomRef.current, zoom: zoomVal };
        }
    }, [map, center, zoom]);

    // Draw the recommended pickup/delivery route (polyline with arrows + numbered markers)
    useEffect(() => {
        if (!map || !window.google?.maps) return undefined;
        const maps = window.google.maps;

        const clearRutaSugerida = () => {
            rutaSugeridaShapesRef.current.forEach((s) => {
                try {
                    if (typeof s?.setMap === "function") s.setMap(null);
                } catch (_) {
                    /* ignore */
                }
            });
            rutaSugeridaShapesRef.current = [];
        };

        clearRutaSugerida();

        const secuencia = Array.isArray(secuenciaSugerida?.secuencia)
            ? secuenciaSugerida.secuencia
            : [];
        if (secuencia.length === 0) return () => clearRutaSugerida();

        const stops = [];
        if (secuenciaSugerida?.startPos) {
            const { lat, lng } = secuenciaSugerida.startPos;
            if (isFinite(lat) && isFinite(lng)) stops.push({ lat, lng });
        }
        secuencia.forEach((s) => {
            const lat = s?.coord?.lat;
            const lng = s?.coord?.lng;
            if (isFinite(lat) && isFinite(lng)) stops.push({ lat, lng });
        });
        if (stops.length < 2) return () => clearRutaSugerida();

        const RUTA_STROKE = "#7c3aed"; // violeta para distinguir de georuta/histórico

        const drawPolyline = (pathToDraw) => {
            const polyline = new maps.Polyline({
                path: pathToDraw,
                geodesic: true,
                strokeColor: RUTA_STROKE,
                strokeOpacity: 0.95,
                strokeWeight: 4,
                icons: [
                    {
                        icon: {
                            path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
                            scale: 3,
                            strokeColor: RUTA_STROKE,
                            strokeWeight: 1.5,
                            fillColor: RUTA_STROKE,
                            fillOpacity: 1,
                        },
                        offset: "0",
                        repeat: "80px",
                    },
                ],
                map,
                zIndex: 6,
            });
            rutaSugeridaShapesRef.current.push(polyline);
        };

        const drawStopMarkers = () => {
            secuencia.forEach((s, i) => {
                const lat = s?.coord?.lat;
                const lng = s?.coord?.lng;
                if (!isFinite(lat) || !isFinite(lng)) return;
                const color = s?.tipo === "origen" ? "#16a34a" : "#dc2626";
                const numero = i + 1;
                const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="34" height="46" viewBox="0 0 34 46">
  <path fill="${color}" stroke="rgba(0,0,0,0.35)" stroke-width="1"
    d="M17 2C9.27 2 3 8 3 15c0 9 14 29 14 29s14-20 14-29C31 8 24.73 2 17 2z"/>
  <circle cx="17" cy="15" r="9" fill="white" />
  <text x="17" y="19" font-family="Arial, sans-serif" font-size="12" font-weight="700"
    text-anchor="middle" fill="${color}">${numero}</text>
</svg>`;
                const icon = {
                    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
                    scaledSize: new maps.Size(30, 40),
                    anchor: new maps.Point(15, 40),
                };
                const marker = new maps.Marker({
                    position: { lat, lng },
                    map,
                    title: `${numero}. ${s?.tipo === "origen" ? t("label_recoger") : t("label_entregar")}: ${s?.geocercaLabel ?? ""}`,
                    icon,
                    zIndex: 7,
                });
                marker.addListener("click", () => {
                    if (typeof onViajeClick === "function") {
                        const viaje = (Array.isArray(viajes) ? viajes : []).find(
                            (v) => String(v?.id) === String(s?.pedidoId)
                        );
                        if (viaje) onViajeClick(viaje);
                    }
                });
                rutaSugeridaShapesRef.current.push(marker);
            });
        };

        let cancelled = false;

        // Markers de paradas se dibujan igual en ambos casos (real o fallback)
        drawStopMarkers();

        // Pedimos ruta drivable a Google Directions; si falla, caemos a la recta
        fetchDirectionsDrivingPath(maps, stops, (drivingPath /*, distanceMeters */) => {
            if (cancelled) return;
            const pathToUse = drivingPath && drivingPath.length >= 2 ? drivingPath : stops;
            drawPolyline(pathToUse);

            // Fit bounds usando el camino que se dibujó (o los stops si no hay driving)
            const bounds = new maps.LatLngBounds();
            pathToUse.forEach((p) => bounds.extend(p));
            try {
                map.fitBounds(bounds);
            } catch (_) {
                /* ignore */
            }
        });

        return () => {
            cancelled = true;
            clearRutaSugerida();
        };
    }, [map, secuenciaSugerida, t, onViajeClick, viajes]);

    // Render geocercas/georuta shapes for each viaje
    useEffect(() => {
        if (!map || !window.google?.maps) return;
        const maps = window.google.maps;

        shapesRef.current.forEach((s) => {
            try {
                s.setMap(null);
            } catch (_) {
                /* ignore */
            }
        });
        shapesRef.current = [];

        const TitleOnlyOverlay = createTitleOnlyOverlay(maps);
        const bounds = new maps.LatLngBounds();
        let anyBounds = false;

        const drawGeocerca = (viaje, geocercaValue, label, defaultColor, isSelected) => {
            if (geocercaValue == null || typeof geocercaValue !== "object") return;
            const rings = coordsToLatLngRings(geocercaValue.coordenadas);
            if (rings.length === 0) return;
            const color = geocercaValue.colorGeocerca || defaultColor;

            rings.forEach((ring) => {
                ring.forEach((p) => {
                    bounds.extend(p);
                    anyBounds = true;
                });
                const polygon = new maps.Polygon({
                    paths: ring,
                    strokeColor: color,
                    strokeOpacity: 0.95,
                    strokeWeight: isSelected ? 3 : 2,
                    fillColor: color,
                    fillOpacity: isSelected ? 0.35 : 0.18,
                    map,
                    zIndex: isSelected ? 3 : 1,
                });
                polygon.addListener("click", () => {
                    if (typeof onViajeClick === "function") onViajeClick(viaje);
                });
                shapesRef.current.push(polygon);
            });

            const centroid = ringCentroid(rings[0]);
            if (centroid && geocercaValue.label) {
                const overlay = new TitleOnlyOverlay(
                    centroid,
                    `${label}: ${geocercaValue.label}`,
                    () => {
                        if (typeof onViajeClick === "function") onViajeClick(viaje);
                    }
                );
                overlay.setMap(map);
                shapesRef.current.push(overlay);
            }
        };

        const drawGeoruta = (viaje, georutaValue, isSelected) => {
            if (georutaValue == null || typeof georutaValue !== "object") return;
            const rings = coordsToLatLngRings(georutaValue.coordenadas);
            if (rings.length === 0) return;
            const color = georutaValue.colorGeocerca || GEORUTA_STROKE;

            rings.forEach((ring) => {
                if (ring.length < 2) return;
                ring.forEach((p) => {
                    bounds.extend(p);
                    anyBounds = true;
                });
                const polyline = new maps.Polyline({
                    path: ring,
                    geodesic: true,
                    strokeColor: color,
                    strokeOpacity: 0.95,
                    strokeWeight: isSelected ? 5 : 3,
                    icons: [
                        {
                            icon: {
                                path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                scale: 2.5,
                                strokeColor: color,
                                strokeWeight: 1.5,
                                fillColor: color,
                                fillOpacity: 1,
                            },
                            offset: "0",
                            repeat: GEORUTA_ARROW_REPEAT,
                        },
                    ],
                    map,
                    zIndex: isSelected ? 2 : 1,
                });
                polyline.addListener("click", () => {
                    if (typeof onViajeClick === "function") onViajeClick(viaje);
                });
                shapesRef.current.push(polyline);
            });
        };

        (Array.isArray(viajes) ? viajes : []).forEach((viaje) => {
            const isSelected =
                viajeSelected != null && String(viajeSelected.id) === String(viaje?.id);
            drawGeocerca(
                viaje,
                getRespuestaByKey(viaje, "geocercaorigen")?.value,
                t("label_geocerca_origen"),
                ORIGEN_DEFAULT_COLOR,
                isSelected
            );
            drawGeocerca(
                viaje,
                getRespuestaByKey(viaje, "geocercadestino")?.value,
                t("label_geocerca_destino"),
                DESTINO_DEFAULT_COLOR,
                isSelected
            );
            drawGeoruta(
                viaje,
                getRespuestaByKey(viaje, "seleccionegeoruta")?.value,
                isSelected
            );
        });

        if (anyBounds && !didInitialFitRef.current) {
            map.fitBounds(bounds);
            didInitialFitRef.current = true;
        }
    }, [map, viajes, viajeSelected, t, onViajeClick]);

    // Fit bounds to the currently selected viaje when selection changes
    useEffect(() => {
        if (!map || !window.google?.maps || viajeSelected == null) return;
        const maps = window.google.maps;
        const bounds = new maps.LatLngBounds();
        let anyBounds = false;

        const addRings = (val) => {
            if (val == null || typeof val !== "object") return;
            coordsToLatLngRings(val.coordenadas).forEach((ring) => {
                ring.forEach((p) => {
                    bounds.extend(p);
                    anyBounds = true;
                });
            });
        };
        addRings(getRespuestaByKey(viajeSelected, "geocercaorigen")?.value);
        addRings(getRespuestaByKey(viajeSelected, "geocercadestino")?.value);
        addRings(getRespuestaByKey(viajeSelected, "seleccionegeoruta")?.value);

        if (anyBounds) map.fitBounds(bounds);
    }, [map, viajeSelected]);

    // Historic GPS polyline for the selected viaje
    useEffect(() => {
        if (!map || !window.google?.maps) return undefined;

        const clearHistoricPolyline = () => {
            if (historicVehiclePolylineRef.current) {
                historicVehiclePolylineRef.current.setMap(null);
                historicVehiclePolylineRef.current = null;
            }
            if (historicVehicleStartInfoWindowRef.current) {
                historicVehicleStartInfoWindowRef.current.close();
                historicVehicleStartInfoWindowRef.current = null;
            }
            if (historicVehicleStartMarkerRef.current) {
                historicVehicleStartMarkerRef.current.setMap(null);
                historicVehicleStartMarkerRef.current = null;
            }
        };

        clearHistoricPolyline();

        if (!Array.isArray(historicVehiclePath) || historicVehiclePath.length < 2) {
            return () => clearHistoricPolyline();
        }

        const maps = window.google.maps;
        const polyline = new maps.Polyline({
            path: historicVehiclePath,
            geodesic: true,
            strokeColor: HISTORIC_VEHICLE_PATH_STROKE,
            strokeOpacity: 1,
            strokeWeight: 3,
            icons: [
                {
                    icon: {
                        path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: 2.5,
                        strokeColor: HISTORIC_VEHICLE_PATH_STROKE,
                        strokeWeight: 1.5,
                        fillColor: HISTORIC_VEHICLE_PATH_STROKE,
                        fillOpacity: 1,
                    },
                    offset: "0",
                    repeat: HISTORIC_VEHICLE_ARROW_REPEAT,
                },
            ],
            map,
            zIndex: 4,
        });
        historicVehiclePolylineRef.current = polyline;

        if (historicVehicleStartPoint != null) {
            const marker = new maps.Marker({
                position: {
                    lat: historicVehicleStartPoint.lat,
                    lng: historicVehicleStartPoint.lng,
                },
                map,
                title:
                    historicVehicleStartPoint.timeText && historicVehicleStartPoint.timeText !== ""
                        ? `${t("label_inicio")}: ${historicVehicleStartPoint.timeText}`
                        : t("label_inicio"),
                icon: getColoredMarkerIcon(HISTORIC_VEHICLE_START_MARKER_COLOR),
                zIndex: 5,
            });
            historicVehicleStartMarkerRef.current = marker;

            const infoContent =
                historicVehicleStartPoint.timeText && historicVehicleStartPoint.timeText !== ""
                    ? `<div style="padding:6px 8px;font-size:12px;"><strong>${t("label_inicio")}:</strong> ${historicVehicleStartPoint.timeText}</div>`
                    : `<div style="padding:6px 8px;font-size:12px;"><strong>${t("label_inicio")}</strong></div>`;
            const infoWindow = new maps.InfoWindow({ content: infoContent, disableAutoPan: true });
            historicVehicleStartInfoWindowRef.current = infoWindow;

            marker.addListener("click", () => {
                infoWindow.open({ anchor: marker, map });
            });
        }

        return () => clearHistoricPolyline();
    }, [map, historicVehiclePath, historicVehicleStartPoint, t]);

    // Fetch historic GPS for the selected viaje's placa in its own date range; poll every 15s
    useEffect(() => {
        setHistoricVehiclePath([]);
        setHistoricVehicleStartPoint(null);

        if (viajeSelected == null) return;
        const placa = getPlacaFromViaje(viajeSelected);
        const iniStr = getFechaHoraInicio(viajeSelected);
        const finStr = getFechaHoraFin(viajeSelected);
        if (!placa || !iniStr || !finStr) return;

        const fechaIni = moment(iniStr, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD HH:mm:ss"], true);
        const fechaFin = moment(finStr, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD HH:mm:ss"], true);
        if (!fechaIni.isValid() || !fechaFin.isValid()) return;

        const objHistorico = {
            fechaHoraIni: fechaIni.format("DD/MM/YYYY HH:mm:ss"),
            fechaHoraFin: fechaFin.format("DD/MM/YYYY HH:mm:ss"),
            placa: String(placa).trim(),
        };

        const gen = (historicoFetchGenerationRef.current += 1);
        let cancelled = false;
        let polling = false;
        let intervalId = null;

        const run = async () => {
            if (cancelled || polling) return;
            polling = true;
            try {
                const response = await obtenerHistorico(objHistorico);
                if (cancelled || gen !== historicoFetchGenerationRef.current) return;
                if (
                    response.error === false &&
                    Array.isArray(response.data) &&
                    response.data.length > 0
                ) {
                    const { path, startPoint } = historicRawToLatLngPath(response.data);
                    if (cancelled || gen !== historicoFetchGenerationRef.current) return;
                    if (path.length >= 2) {
                        setHistoricVehiclePath(path);
                        setHistoricVehicleStartPoint(startPoint);
                    } else {
                        setHistoricVehiclePath([]);
                        setHistoricVehicleStartPoint(null);
                    }
                } else {
                    setHistoricVehiclePath([]);
                    setHistoricVehicleStartPoint(null);
                }
            } catch (_) {
                if (cancelled || gen !== historicoFetchGenerationRef.current) return;
                setHistoricVehiclePath([]);
                setHistoricVehicleStartPoint(null);
            } finally {
                polling = false;
            }
        };

        run();
        intervalId = window.setInterval(run, 15000);
        return () => {
            cancelled = true;
            if (intervalId != null) window.clearInterval(intervalId);
            historicoFetchGenerationRef.current += 1;
        };
    }, [viajeSelected]);

    // Vehicle position marker (parent must provide puntoActual for the selected viaje)
    useEffect(() => {
        if (!map || !window.google?.maps) return;
        const { maps } = window.google;

        if (puntoActualMarkerRef.current) {
            puntoActualMarkerRef.current.setMap(null);
            puntoActualMarkerRef.current = null;
        }
        if (puntoActualLabelRef.current) {
            puntoActualLabelRef.current.setMap(null);
            puntoActualLabelRef.current = null;
        }

        if (puntoActual == null || typeof puntoActual !== "object") return;

        const lat = parseFloat(puntoActual.LATITUD ?? puntoActual.lat ?? puntoActual.latitud);
        const lng = parseFloat(puntoActual.LONGITUD ?? puntoActual.lng ?? puntoActual.longitud);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return;

        const iconUrl = getVehicleIconUrl(puntoActual);

        const marker = new maps.Marker({
            map,
            position: { lat, lng },
            title: "Posición actual del vehículo",
            icon: {
                url: iconUrl,
                scaledSize: new maps.Size(PUNTO_ACTUAL_MARKER_SIZE, PUNTO_ACTUAL_MARKER_SIZE),
                anchor: new maps.Point(PUNTO_ACTUAL_MARKER_SIZE / 2, PUNTO_ACTUAL_MARKER_SIZE / 2),
            },
            zIndex: 10,
        });
        puntoActualMarkerRef.current = marker;

        const nombre =
            puntoActual.nombre ??
            puntoActual.NOMBRE ??
            (viajeSelected ? getPlacaFromViaje(viajeSelected) : null) ??
            "";
        if (nombre) {
            const LabelBelowOverlay = createLabelBelowOverlay(maps);
            const labelOverlay = new LabelBelowOverlay(
                { lat, lng },
                nombre,
                PUNTO_ACTUAL_LABEL_OFFSET_Y
            );
            labelOverlay.setMap(map);
            puntoActualLabelRef.current = labelOverlay;
        }
    }, [map, puntoActual, viajeSelected]);

    // Markers for every other vehicle that has visible cards (excluding the selected viaje's placa)
    useEffect(() => {
        if (!map || !window.google?.maps) return;
        const { maps } = window.google;

        vehiculosShapesRef.current.forEach((s) => {
            try {
                if (typeof s?.setMap === "function") s.setMap(null);
            } catch (_) {
                /* ignore */
            }
        });
        vehiculosShapesRef.current = [];

        const items = Array.isArray(vehiculosPuntos) ? vehiculosPuntos : [];
        const SIZE = Math.round(PUNTO_ACTUAL_MARKER_SIZE * 0.75);
        const LabelBelowOverlay = createLabelBelowOverlay(maps);

        items.forEach(({ placa, punto }) => {
            if (punto == null || typeof punto !== "object") return;
            const lat = parseFloat(punto.LATITUD ?? punto.lat ?? punto.latitud);
            const lng = parseFloat(punto.LONGITUD ?? punto.lng ?? punto.longitud);
            if (Number.isNaN(lat) || Number.isNaN(lng)) return;

            const iconUrl = getVehicleIconUrl(punto);

            const marker = new maps.Marker({
                map,
                position: { lat, lng },
                title: placa,
                icon: {
                    url: iconUrl,
                    scaledSize: new maps.Size(SIZE, SIZE),
                    anchor: new maps.Point(SIZE / 2, SIZE / 2),
                },
                zIndex: 8,
            });
            vehiculosShapesRef.current.push(marker);

            const nombre = punto.nombre ?? punto.NOMBRE ?? placa ?? "";
            if (nombre) {
                const labelOverlay = new LabelBelowOverlay(
                    { lat, lng },
                    nombre,
                    Math.round(SIZE / 3)
                );
                labelOverlay.setMap(map);
                vehiculosShapesRef.current.push(labelOverlay);
            }
        });

        return () => {
            vehiculosShapesRef.current.forEach((s) => {
                try {
                    if (typeof s?.setMap === "function") s.setMap(null);
                } catch (_) {
                    /* ignore */
                }
            });
            vehiculosShapesRef.current = [];
        };
    }, [map, vehiculosPuntos]);

    return (
        <div className="relative w-full h-full" style={{ minHeight: "400px" }}>
            <div ref={mapRef} id="mapa-viaje-google" className="w-full h-full" />

            <div className="absolute top-3 left-3 z-10 bg-white/95 border border-gray-200 rounded-md shadow-sm px-3 py-2 text-xs text-gray-700 mt-12">
                <div className="flex items-center gap-2 whitespace-nowrap">
                    <span
                        className="inline-block w-3 h-3 rounded-sm"
                        style={{ backgroundColor: ORIGEN_DEFAULT_COLOR, opacity: 0.6 }}
                        aria-hidden
                    />
                    <span>{t("label_geocerca_origen")}</span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap mt-1">
                    <span
                        className="inline-block w-3 h-3 rounded-sm"
                        style={{ backgroundColor: DESTINO_DEFAULT_COLOR, opacity: 0.6 }}
                        aria-hidden
                    />
                    <span>{t("label_geocerca_destino")}</span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap mt-1">
                    <span
                        className="inline-block w-6 h-[3px] rounded"
                        style={{ backgroundColor: GEORUTA_STROKE }}
                        aria-hidden
                    />
                    <span>{t("label_georuta")}</span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap mt-1">
                    <span
                        className="inline-block w-6 h-[3px] rounded"
                        style={{ backgroundColor: HISTORIC_VEHICLE_PATH_STROKE }}
                        aria-hidden
                    />
                    <span>{t("label_ruta_vehiculo")}</span>
                </div>
                {secuenciaSugerida?.secuencia?.length > 0 && (
                    <div className="flex items-center gap-2 whitespace-nowrap mt-1">
                        <span
                            className="inline-block w-6 h-[3px] rounded"
                            style={{ backgroundColor: "#7c3aed" }}
                            aria-hidden
                        />
                        <span>{t("label_orden_recomendado")}</span>
                    </div>
                )}
            </div>
        </div>
    );
}
